import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useEffect, useMemo } from "react";
import { fetchData, post, patch, del } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { CommandSessionList, CommandSession } from "@/types/command-agent";
import { toast } from "sonner";

const friendlyErrorDescription = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
    if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
    if (Array.isArray(data?.detail) && typeof data.detail[0]?.msg === "string") return data.detail[0].msg;
    if (typeof data?.error === "string" && data.error.trim()) return data.error;
    return fallback;
};

export function useCommandSessions(
    agentId: string | null,
    options?: { search?: string }
) {
    const queryClient = useQueryClient();
    const search = options?.search?.trim() || "";
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        triggerOnce: false,
    });

    const { hasPermission: canListSessions } = useProtectedQuery({
        queryKey: ["_gated_list_command_sessions"],
        queryFn: async () => null,
        requiredPermissions: ["LIST_COMMAND_SESSIONS"],
        enabled: false,
    });

    const {
        data: sessionsData,
        isLoading: isLoadingSessions,
        isFetching: isFetchingSessions,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<CommandSessionList>({
        queryKey: ["command-sessions", agentId, search],
        initialPageParam: 1,
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: "10",
                sort_by: "updated_at",
                sort_order: "desc",
            });
            if (search) params.set("search", search);
            const response = await fetchData(
                `/api/command-team/agents/${agentId}/sessions?${params.toString()}`
            );
            return response;
        },
        getNextPageParam: (lastPage, pages) => {
            if (pages.length < lastPage.page_count) {
                return pages.length + 1;
            }
            return undefined;
        },
        enabled: canListSessions && Boolean(agentId),
    });

    const allSessions = useMemo(() => {
        if (!sessionsData?.pages) return [];
        const raw = sessionsData.pages.flatMap((page) => page.items);
        const seen = new Set<string>();
        const sessions = raw.filter((s) => { if (seen.has(s.id)) return false; seen.add(s.id); return true; });
        return sessions.sort((a, b) => {
            if (a.pinned && !b.pinned) return -1;
            if (!a.pinned && b.pinned) return 1;
            const aTime = new Date(a.updated_at ?? a.created_at).getTime();
            const bTime = new Date(b.updated_at ?? b.created_at).getTime();
            return bTime - aTime;
        });
    }, [sessionsData]);

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const createSessionMutation = useMutation({
        mutationFn: async () => {
            const response = await post(`/api/command-team/agents/${agentId}/sessions`, {});
            return response.data as CommandSession;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["command-sessions", agentId] });
            toast.success("New session created");
        },
        onError: (error: any) => {
            toast.error("Failed to create session", {
                description: friendlyErrorDescription(error, "We couldn't start a new chat. Please try again."),
            });
        },
    });

    // Update session (pin/unpin or title) mutation
    const updateSessionMutation = useMutation({
        mutationFn: async ({ sessionId, pinned, title }: { sessionId: string; pinned?: boolean; title?: string }) => {
            const payload: { pinned?: boolean; title?: string } = {};
            if (pinned !== undefined) payload.pinned = pinned;
            if (title !== undefined) payload.title = title;
            const response = await patch(`/api/command-team/agents/${agentId}/sessions/${sessionId}`, payload);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["command-sessions", agentId] });
        },
        onError: (error: any) => {
            toast.error("Failed to update session", {
                description: friendlyErrorDescription(error, "We couldn't update this chat. Please try again."),
            });
        },
    });

    // Delete session mutation
    const deleteSessionMutation = useMutation({
        mutationFn: async (sessionId: string) => {
            const response = await del(`/api/command-team/agents/${agentId}/sessions/${sessionId}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["command-sessions", agentId] });
            toast.success("Session deleted successfully");
        },
        onError: (error: any) => {
            toast.error("Failed to delete session", {
                description: friendlyErrorDescription(error, "We couldn't delete this chat. Please try again."),
            });
        },
    });

    return {
        allSessions,
        isLoadingSessions,
        isFetchingSessions,
        isFetchingNextPage,
        hasNextPage,
        loadMoreRef,
        canListSessions,
        createSession: createSessionMutation.mutate,
        isCreatingSession: createSessionMutation.isPending,
        updateSession: updateSessionMutation.mutate,
        isUpdatingSession: updateSessionMutation.isPending,
        deleteSession: deleteSessionMutation.mutate,
        isDeletingSession: deleteSessionMutation.isPending,
    };
}

